import React, { useState } from 'react';
import { Card, Badge, Button, Modal, Alert, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile,
  faFilePdf,
  faFileWord,
  faFileAlt,
  faTrash,
  faEye,
  faDownload,
  faRobot,
  faBrain,
  faSpinner,
  faCheckCircle,
  faExclamationTriangle,
  faCalendar,
  faWeight,
  faSearch,
  faQuoteLeft
} from '@fortawesome/free-solid-svg-icons';

interface UploadedDocument {
  document_id: string;
  filename: string;
  document_type: string;
  file_size: number;
  file_size_formatted: string;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  description?: string;
  search_tags?: string;
  upload_date: string;
  processed_at?: string;
  analysis_summary?: {
    purpose: string;
    key_findings: string;
    business_insights: any;
    content_categories: string[];
    usage_scenarios: string[];
    searchable_keywords: string[];
  };
  access_count?: number;
  last_accessed?: string;
}

interface UploadedDocumentsListProps {
  documents: UploadedDocument[];
  onDelete: (documentId: string) => void;
  onView?: (document: UploadedDocument) => void;
  loading?: boolean;
}

const UploadedDocumentsList: React.FC<UploadedDocumentsListProps> = ({
  documents,
  onDelete,
  onView,
  loading = false
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<UploadedDocument | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<UploadedDocument | null>(null);

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return faFilePdf;
      case 'doc':
      case 'docx':
        return faFileWord;
      case 'txt':
        return faFileAlt;
      default:
        return faFile;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>;
      case 'processing':
        return (
          <Badge bg="warning">
            <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
            Processing
          </Badge>
        );
      case 'completed':
        return (
          <Badge bg="success">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            AI Ready
          </Badge>
        );
      case 'failed':
        return (
          <Badge bg="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
            Failed
          </Badge>
        );
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'buyer_guide': 'Buyer Guide',
      'seller_guide': 'Seller Guide',
      'contract_template': 'Contract Template',
      'education_material': 'Education Material',
      'process_guide': 'Process Guide',
      'checklist': 'Checklist',
      'marketing_material': 'Marketing Material',
      'legal_document': 'Legal Document',
      'form_template': 'Form Template',
      'other': 'Other'
    };
    
    return typeLabels[type] || type;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteClick = (document: UploadedDocument) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (documentToDelete) {
      onDelete(documentToDelete.document_id);
      setDocumentToDelete(null);
    }
    setShowDeleteModal(false);
  };

  const handleViewAnalysis = (document: UploadedDocument) => {
    setSelectedDocument(document);
    setShowAnalysisModal(true);
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-muted" />
        <p className="text-muted mt-2">Loading documents...</p>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <Card className="text-center py-5" style={{ background: 'rgba(139, 92, 246, 0.05)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
        <Card.Body>
          <FontAwesomeIcon icon={faFile} size="3x" className="text-muted mb-3" />
          <h5 className="text-muted">No Documents Uploaded</h5>
          <p className="text-muted mb-0">
            Upload your business materials to enable AI-powered personalization in your workflows.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <div className="uploaded-documents-list">
        <Row className="g-3">
          {documents.map((document) => (
            <Col key={document.document_id} lg={6} xl={4}>
              <Card 
                className="document-card h-100"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
                  border: '1px solid rgba(139, 92, 246, 0.2)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease'
                }}
              >
                <Card.Header 
                  className="border-bottom-0 pb-2"
                  style={{ background: 'transparent' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon 
                        icon={getFileIcon(document.filename)} 
                        className="me-2" 
                        style={{ color: '#8b5cf6' }}
                        size="lg"
                      />
                      <Badge 
                        bg="light" 
                        text="dark" 
                        className="px-2 py-1"
                        style={{ fontSize: '10px', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                      >
                        {getDocumentTypeLabel(document.document_type)}
                      </Badge>
                    </div>
                    {getStatusBadge(document.processing_status)}
                  </div>
                </Card.Header>
                
                <Card.Body className="pt-2">
                  <h6 className="fw-semibold mb-2" style={{ color: '#374151' }}>
                    {document.filename}
                  </h6>
                  
                  {document.description && (
                    <p className="text-muted small mb-2">
                      {document.description}
                    </p>
                  )}
                  
                  <div className="document-metadata mb-3">
                    <Row className="g-2">
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faWeight} className="me-1 text-muted" size="sm" />
                          <small className="text-muted">{document.file_size_formatted}</small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faCalendar} className="me-1 text-muted" size="sm" />
                          <small className="text-muted">
                            {formatDate(document.upload_date).split(',')[0]}
                          </small>
                        </div>
                      </Col>
                    </Row>
                    
                    {document.access_count !== undefined && document.access_count > 0 && (
                      <div className="mt-2">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faSearch} className="me-1 text-success" size="sm" />
                          <small className="text-success">
                            Used {document.access_count} time{document.access_count !== 1 ? 's' : ''} in automation
                          </small>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* AI Analysis Summary */}
                  {document.processing_status === 'completed' && document.analysis_summary && (
                    <div 
                      className="ai-summary mb-3 p-2"
                      style={{
                        background: 'rgba(16, 185, 129, 0.05)',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        borderRadius: '8px'
                      }}
                    >
                      <div className="d-flex align-items-center mb-1">
                        <FontAwesomeIcon icon={faRobot} className="me-1 text-success" size="sm" />
                        <small className="text-success fw-semibold">AI Analysis Available</small>
                      </div>
                      
                      {document.analysis_summary.usage_scenarios && document.analysis_summary.usage_scenarios.length > 0 && (
                        <div className="usage-scenarios">
                          <small className="text-muted d-block" style={{ fontSize: '11px' }}>
                            <strong>Best for:</strong> {document.analysis_summary.usage_scenarios.slice(0, 2).join(', ')}
                            {document.analysis_summary.usage_scenarios.length > 2 && '...'}
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Keywords */}
                  {document.search_tags && (
                    <div className="keywords mb-3">
                      <small className="text-muted d-block mb-1" style={{ fontSize: '11px' }}>
                        <strong>Keywords:</strong>
                      </small>
                      <div className="d-flex flex-wrap gap-1">
                        {document.search_tags.split(' ').slice(0, 4).map((tag, index) => (
                          <Badge 
                            key={index}
                            bg="light" 
                            text="muted" 
                            style={{ fontSize: '9px' }}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card.Body>
                
                <Card.Footer className="border-top-0 pt-0">
                  <div className="d-flex gap-2">
                    {document.processing_status === 'completed' && document.analysis_summary && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewAnalysis(document)}
                        className="flex-fill"
                      >
                        <FontAwesomeIcon icon={faEye} className="me-1" />
                        View Analysis
                      </Button>
                    )}
                    
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteClick(document)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faTrash} className="me-2 text-danger" />
            Delete Document
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="d-flex align-items-start">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 mt-1" />
            <div>
              <strong>This action cannot be undone</strong>
              <div className="mt-2">
                • Document will be removed from storage<br/>
                • AI analysis data will be deleted<br/>
                • Future workflows won't have access to this content
              </div>
            </div>
          </Alert>
          
          {documentToDelete && (
            <p className="mb-0">
              Are you sure you want to delete <strong>{documentToDelete.filename}</strong>?
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            <FontAwesomeIcon icon={faTrash} className="me-2" />
            Delete Document
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Analysis Details Modal */}
      <Modal 
        show={showAnalysisModal} 
        onHide={() => setShowAnalysisModal(false)} 
        size="lg" 
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <FontAwesomeIcon icon={faBrain} className="me-2 text-primary" />
            AI Analysis Results
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && selectedDocument.analysis_summary && (
            <div className="analysis-details">
              <div className="document-info mb-4">
                <h5 className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon 
                    icon={getFileIcon(selectedDocument.filename)} 
                    className="me-2" 
                    style={{ color: '#8b5cf6' }}
                  />
                  {selectedDocument.filename}
                </h5>
                <Badge bg="primary" className="me-2">
                  {getDocumentTypeLabel(selectedDocument.document_type)}
                </Badge>
                <Badge bg="success">
                  <FontAwesomeIcon icon={faRobot} className="me-1" />
                  AI Analyzed
                </Badge>
              </div>

              {/* Purpose */}
              {selectedDocument.analysis_summary.purpose && (
                <div className="analysis-section mb-4">
                  <h6 className="fw-semibold mb-2">
                    <FontAwesomeIcon icon={faQuoteLeft} className="me-2 text-primary" />
                    Document Purpose
                  </h6>
                  <p className="text-muted mb-0">
                    {selectedDocument.analysis_summary.purpose}
                  </p>
                </div>
              )}

              {/* Key Findings */}
              {selectedDocument.analysis_summary.key_findings && (
                <div className="analysis-section mb-4">
                  <h6 className="fw-semibold mb-2">Key Findings</h6>
                  <div 
                    className="p-3"
                    style={{
                      background: 'rgba(139, 92, 246, 0.05)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '8px'
                    }}
                  >
                    <small className="text-muted">
                      {selectedDocument.analysis_summary.key_findings}
                    </small>
                  </div>
                </div>
              )}

              {/* Content Categories */}
              {selectedDocument.analysis_summary.content_categories && 
               selectedDocument.analysis_summary.content_categories.length > 0 && (
                <div className="analysis-section mb-4">
                  <h6 className="fw-semibold mb-2">Content Categories</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedDocument.analysis_summary.content_categories.map((category, index) => (
                      <Badge key={index} bg="info" className="px-3 py-2">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Usage Scenarios */}
              {selectedDocument.analysis_summary.usage_scenarios && 
               selectedDocument.analysis_summary.usage_scenarios.length > 0 && (
                <div className="analysis-section mb-4">
                  <h6 className="fw-semibold mb-2">Recommended Use Cases</h6>
                  <div className="usage-scenarios">
                    {selectedDocument.analysis_summary.usage_scenarios.map((scenario, index) => (
                      <div key={index} className="d-flex align-items-center mb-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" size="sm" />
                        <span className="text-muted">{scenario.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Searchable Keywords */}
              {selectedDocument.analysis_summary.searchable_keywords && 
               selectedDocument.analysis_summary.searchable_keywords.length > 0 && (
                <div className="analysis-section mb-0">
                  <h6 className="fw-semibold mb-2">Searchable Keywords</h6>
                  <div className="d-flex flex-wrap gap-1">
                    {selectedDocument.analysis_summary.searchable_keywords.map((keyword, index) => (
                      <Badge key={index} bg="light" text="dark" style={{ fontSize: '11px' }}>
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAnalysisModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Component Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          .document-card {
            transition: all 0.3s ease;
          }
          
          .document-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
            border-color: rgba(139, 92, 246, 0.4) !important;
          }
          
          .analysis-section {
            border-bottom: 1px solid rgba(0, 0, 0, 0.1);
            padding-bottom: 1rem;
          }
          
          .analysis-section:last-child {
            border-bottom: none;
            padding-bottom: 0;
          }
        `
      }} />
    </>
  );
};

export default UploadedDocumentsList;